import { AddCircle as AddCircleIcon } from '@material-ui/icons';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import QCXFocusLock from '../../../components/focus-lock/QCXFocusLock';
import QCXMainLayout from '../../../components/main-layout/QCXMainLayout';
import {
  confirmOperation,
  resetOperation,
  selectOperationActive,
  selectOperationMessage,
  selectOperationTitle,
} from '../../../features/controller/controllerSlice';
import { focusLockActions } from '../../../features/focus-lock/focusLockSlice';
import QCXProgressBackdrop from '../../../shared-components/backdrop/QCXProgressBackdrop';
import QCXConfirmDialog from '../../../shared-components/dialog/QCXConfirmDialog';
import QCXErrorSnackbar from '../../../shared-components/snackbar/QCXErrorSnackbar';
import QCXSuccessSnackbar from '../../../shared-components/snackbar/QCXSuccessSnackbar';
import DataGridControl from './DataGridControl';

export default function SimpleConsultPageTemplate({ ...props }) {
  const { t } = useTranslation();
  const {
    pageProps: { icon, title, breadcrumbs = [] },
    tableProps: { columns = [], ...restTableProps },
    controlProps: { buttonNew, others = [] } = { buttonNew: null, others: [] },
    searchGridSize,
    buttonGridSize,
    tableData = [],
    entityPluralName,
    isLoading = false,
    openBackdrop = false,
    successSnackbar,
    errorSnackbar,
    handleCloseSuccessSnackbar,
    handleCloseErrorSnackbar,
    multipleFilter = false,
    filterProps = [],
    buttonNewText = t('com.muralis.qcx.acoes.novo'),
    focusLockProps = {},
    children,
    alertMessage,
    authInfo = {},
    requiredRoles = [],
    showInactiveRows = false,
    advancedFilterFormChildren = null,
    advancedFilterKeyAccessors = [],
  } = props;

  const dispatch = useDispatch();

  const operationActive = useSelector(selectOperationActive);
  const operationTitle = useSelector(selectOperationTitle);
  const operationMessage = useSelector(selectOperationMessage);

  const handleConfirmDialog = () => {
    dispatch(confirmOperation());
  };

  const handleCloseDialog = () => {
    dispatch(resetOperation());
  };

  const createPermission = useMemo(
    () =>
      requiredRoles.every((usrRole) => {
        const role = `${usrRole}-incluir`;
        return authInfo?.roles?.includes(role);
      }),
    [authInfo, requiredRoles]
  );

  useEffect(() => {
    let unmount = false;

    const handleFocusLock = () => {
      if (!unmount) {
        dispatch(focusLockActions.lock());
      }
    };

    handleFocusLock();

    return () => {
      unmount = true;
    };
  }, []);

  return (
    <QCXMainLayout pageIcon={icon} pageTitle={title} breadcrumbs={breadcrumbs} authInfo={authInfo}>
      <QCXFocusLock {...focusLockProps}>
        {alertMessage}
        <DataGridControl
          columns={columns}
          rows={tableData}
          loading={isLoading}
          entityPluralName={entityPluralName}
          multipleFilter={multipleFilter}
          filterProps={filterProps}
          showInactiveRows={showInactiveRows}
          searchGridSize={searchGridSize}
          buttonGridSize={buttonGridSize}
          advancedFilterFormChildren={advancedFilterFormChildren}
          advancedFilterKeyAccessors={advancedFilterKeyAccessors}
          controlButtons={[
            ...(buttonNew
              ? [
                  {
                    to: buttonNew?.path,
                    description: buttonNewText,
                    startIcon: <AddCircleIcon />,
                    tooltip: {
                      description: createPermission
                        ? buttonNew?.description
                        : t('com.muralis.validacao.usuarioSemAutorizacao'),
                    },
                    onClick: buttonNew?.onClick,
                    disabled: buttonNew?.disabled || !createPermission,
                  },
                ]
              : []),
            ...others,
          ]}
          {...restTableProps}
        />
      </QCXFocusLock>
      <div id="portal-elements">
        {successSnackbar?.open && (
          <QCXSuccessSnackbar
            open={successSnackbar?.open}
            content={successSnackbar?.message}
            handleClose={handleCloseSuccessSnackbar}
            {...successSnackbar}
          />
        )}
        {errorSnackbar?.open && (
          <QCXErrorSnackbar
            open={errorSnackbar?.open}
            content={errorSnackbar?.message}
            handleClose={handleCloseErrorSnackbar}
            {...errorSnackbar}
          />
        )}
        <QCXProgressBackdrop open={openBackdrop} />
        <QCXConfirmDialog
          open={operationActive}
          title={operationTitle}
          content={operationMessage}
          onClose={handleCloseDialog}
          onConfirm={handleConfirmDialog}
        />
        {children}
      </div>
    </QCXMainLayout>
  );
}
